/**
 * Farmland's COA Automation module's own per-operation functions, over the transport the framework hands
 * it (FLF-S1).
 *
 * This is the shape every module frontend copies, following `web/modules/skeleton/src/notes.ts` in
 * `EquerraNZ/eq-portal`: named functions plus a query-key factory, so a component never writes a path, a
 * method or a query key, and a route the portal does not serve is a build error rather than a 404 in a
 * browser.
 *
 * The types come from `./generated/coaApi.js`, generated from **this portal's own** built API host's
 * OpenAPI contract (`artifacts/openapi/FlfCoaWebapp.Api.json`, itself generated at build time from
 * `FlfCoaWebapp.Api`'s own composition), following the framework's own S36-D4 rule: a module's client can
 * only be generated from the document of a portal that composed it.
 */
import { coaListParams } from './coaLists.js';
/**
 * This module's slug, which must equal the one `Equerra.Portal.Modules.Coa` declares.
 *
 * The one value the two halves share. It is a literal type as well as a value, because the transport's
 * path filter is keyed on it: the type is what makes `/v1/coa/...` the only paths this module can name,
 * and the value is what the framework checks its own binding against.
 */
export const COA_SLUG = 'coa';
/**
 * Query keys as a factory rather than as literals at each call site.
 *
 * Prefixed with the module's slug, so two modules' caches cannot collide and neither can collide with the
 * framework's own `['portal', ...]` keys.
 *
 * `acceptanceReferenceList` is the key the page's interactive list caches each view under (`usePortalModuleList` appends
 * the view), a sub-key of `acceptanceReferences` and never a new top-level key: a write invalidates the base key, and
 * TanStack's prefix match then refreshes every cached view of the list and any other read under that base.
 */
export const coaQueryKeys = {
    all: () => ['module', COA_SLUG],
    acceptanceReferences: () => ['module', COA_SLUG, 'acceptance-references'],
    acceptanceReferenceList: () => ['module', COA_SLUG, 'acceptance-references', 'list'],
};
/**
 * Reads this tenant's acceptance-reference entries. With no `query` it asks for the default view, exactly as it always
 * has: the newest page, with no query string. With one it asks for a page on the shared list contract.
 */
export function listAcceptanceReferences(transport, options) {
    return transport.send({
        method: 'GET',
        path: '/v1/coa/acceptance-references',
        ...coaListParams(options?.query),
        ...(options?.signal === undefined ? {} : { signal: options.signal }),
    });
}
/** Creates an acceptance-reference entry for one lab test. */
export function createAcceptanceReference(transport, request) {
    return transport.send({
        method: 'POST',
        path: '/v1/coa/acceptance-references',
        body: request,
    });
}
/** Deletes one acceptance-value reference entry for this tenant. */
export function deleteAcceptanceReference(transport, id) {
    return transport.send({
        method: 'DELETE',
        path: '/v1/coa/acceptance-references/{id}',
        params: { path: { id } },
    });
}
/**
 * The read, shaped for TanStack Query, so a component passes one object to `useQuery`.
 *
 * Typed structurally rather than against TanStack's own types, for the reason the framework's own
 * operations give: an operation module should not need the query library to describe what it returns.
 */
export function acceptanceReferencesQueryOptions(transport) {
    return {
        queryKey: coaQueryKeys.acceptanceReferences(),
        queryFn: (context) => listAcceptanceReferences(transport, { signal: context.signal }),
    };
}
//# sourceMappingURL=acceptanceReferences.js.map